import { Injectable } from '@angular/core';
import { BcmService } from './bcm.service';
import { BcmTenantPermission } from './bcm-tenant-permission';
import {
    areAllLicensedReceiptPrinterTypesAtQuota,
    canAddReceiptPrinterOfType,
    filterUsableReceiptPrinters,
    getModuleLicense,
    getPrinterTypeOptionDisabledReason,
    hasActiveModuleLicense,
    hasAnyPosPrinterModuleLicense,
    isReceiptPrinterLicensed,
    mapReceiptPrinterTypeToModulePermission,
    PosPrinterLicenseUsageView,
    PosReceiptPrinterType,
    resolveReceiptPrinterTypeForForm,
    TenantModuleLicenseView,
} from '@modules/bcm/settings/network-printers/pos-printer-license.util';
import {
    canEnableDocksiteUser,
    countDocksiteEnabledUsers,
    DocksiteLicenseUsageView,
    formatDocksiteQuotaLabel,
    getDocksiteModuleLicense,
    getDocksiteToggleDisabledReason,
    isUnlimitedDocksiteQuota,
} from '@modules/bcm/users/docksite-user-license.util';
import { BcmNetworkPrinter } from '@shared/models/network-printer';
import { anyAccessibleTenantHasMultilingualism, resolveEffectiveUserLanguage } from '@modules/bcm/@shared/utils/multilingualism-license.util';
import { TenantApiService } from '@bcmApiServices/tenant.api-service';
import { TolgeeService } from '@core/translation/tolgee.service';
import { Location } from '@angular/common';
import { getTenantIdFromUrl } from '@modules/bcm/@shared/utils/tenant-context.util';

@Injectable({ providedIn: 'root' })
export class BcmTenantService {

    constructor(
        private bcmService: BcmService,
        private tenantApiService: TenantApiService,
        private tolgeeService: TolgeeService,
        private location: Location,
    ) {
    }

    public get moduleLicenses(): TenantModuleLicenseView[] {
        return this.bcmService.tenant?.moduleLicenses ?? [];
    }

    public get printerLicenseUsage(): PosPrinterLicenseUsageView {
        return this.bcmService.tenant?.printerLicenseUsage ?? { escpos: 0, eposXml: 0 };
    }

    public get docksiteLicenseUsage(): DocksiteLicenseUsageView {
        return this.bcmService.tenant?.docksiteLicenseUsage ?? { enabled: 0 };
    }

    public hasAllPermissionsOf(...permissions: BcmTenantPermission[]): boolean {

        let result = true;

        for (const permission of permissions) {
            result = this.checkPermission(permission);

            if (!result) {
                break;
            }
        }

        return result;
    }

    public hasOnePermissionOf(...permissions: BcmTenantPermission[]): boolean {

        let result = false;

        for (const permission of permissions) {
            result = this.checkPermission(permission);

            if (result) {
                break;
            }
        }

        return result === true;
    }

    public checkPermission(permission: BcmTenantPermission): boolean {
        return permission == null || (this.bcmService.tenant?.permissions || []).includes(permission);
    }

    /**
     * Multisprachigkeit (#6601): aktiver Mandant → dessen Modulrecht;
     * ohne aktiven Mandant (Übersicht) → mindestens ein zugänglicher Mandant.
     */
    public hasMultilingualismLicense(): boolean {
        if (this.bcmService.tenant?.id) {
            return this.checkPermission(BcmTenantPermission.MULTILINGUALISM);
        }

        return anyAccessibleTenantHasMultilingualism(this.bcmService.accessibleTenants);
    }

    /**
     * Lädt die Mandantenliste für Modulchecks außerhalb eines Mandanten (#6601).
     * Ohne aktiven Mandanten entscheidet sie über das Sprachmodul-Recht — fehlt sie,
     * bekommt ein lizenzierter Nutzer beim Sprachwechsel den Verkaufsdialog.
     */
    public preloadAccessibleTenantsIfNeeded(): void {
        // Im Mandanten-Kontext zählt nur das Modulrecht des aktiven Mandanten; die ganze
        // Liste zu holen wäre Over-Fetching (#6847) — GET /tenants entschlüsselt Bankdaten.
        if (getTenantIdFromUrl(this.location)) {
            return;
        }

        if (this.bcmService.tenant?.id || this.bcmService.accessibleTenants.length) {
            return;
        }

        this.tenantApiService.getAll().subscribe(tenants => {
            this.bcmService.setAccessibleTenants(tenants);
            void this.applyStoredUserLanguageIfNeeded();
        });
    }

    /** Wendet die nutzer-weite Sprachpräferenz an (localStorage), wenn erlaubt. */
    public async applyStoredUserLanguageIfNeeded(): Promise<void> {
        const language = resolveEffectiveUserLanguage(undefined, this);

        if (language !== this.tolgeeService.getLanguage()) {
            await this.tolgeeService.changeLanguage(language);
        }
    }

    public hasActiveModuleLicense(permission: BcmTenantPermission): boolean {
        return hasActiveModuleLicense(this.moduleLicenses, permission);
    }

    public getModuleLicense(permission: BcmTenantPermission): TenantModuleLicenseView | undefined {
        return getModuleLicense(this.moduleLicenses, permission);
    }

    public hasAnyPosPrinterModuleLicense(): boolean {
        return hasAnyPosPrinterModuleLicense(this.moduleLicenses);
    }

    public canAddReceiptPrinterOfType(printerType: PosReceiptPrinterType): boolean {
        return canAddReceiptPrinterOfType(this.moduleLicenses, this.printerLicenseUsage, printerType);
    }

    public isReceiptPrinterLicensed(printer: Pick<BcmNetworkPrinter, 'printerType'>): boolean {
        return isReceiptPrinterLicensed(this.moduleLicenses, printer);
    }

    public areAllLicensedReceiptPrinterTypesAtQuota(): boolean {
        return areAllLicensedReceiptPrinterTypesAtQuota(this.moduleLicenses, this.printerLicenseUsage);
    }

    public getReceiptPrinterModulePermission(printerType: PosReceiptPrinterType): BcmTenantPermission | null {
        return mapReceiptPrinterTypeToModulePermission(printerType);
    }

    public getPrinterTypeOptionDisabledReason(printerType: PosReceiptPrinterType): 'missingLicense' | 'quotaExceeded' | null {
        return getPrinterTypeOptionDisabledReason(this.moduleLicenses, this.printerLicenseUsage, printerType);
    }

    public filterUsableReceiptPrinters(printers: BcmNetworkPrinter[], userId: string): BcmNetworkPrinter[] {
        return filterUsableReceiptPrinters(printers, this.moduleLicenses, userId);
    }

    public resolveReceiptPrinterTypeForForm(
        existingPrinterType: string | null | undefined,
    ): PosReceiptPrinterType {
        return resolveReceiptPrinterTypeForForm(
            existingPrinterType,
            this.moduleLicenses,
            this.printerLicenseUsage,
        );
    }

    public hasDocksiteModuleLicense(): boolean {
        return this.checkPermission(BcmTenantPermission.DOCKSITE);
    }

    public getDocksiteModuleLicense(): TenantModuleLicenseView | undefined {
        return getDocksiteModuleLicense(this.moduleLicenses);
    }

    public isUnlimitedDocksiteQuota(): boolean {
        return isUnlimitedDocksiteQuota(this.getDocksiteModuleLicense()?.quota);
    }

    public canEnableDocksiteUser(enabledCount: number, isCurrentlyEnabled: boolean): boolean {
        return canEnableDocksiteUser(this.moduleLicenses, enabledCount, isCurrentlyEnabled);
    }

    public getDocksiteToggleDisabledReason(enabledCount: number, isCurrentlyEnabled: boolean): 'quotaExceeded' | null {
        return getDocksiteToggleDisabledReason(this.moduleLicenses, enabledCount, isCurrentlyEnabled);
    }

    public formatDocksiteQuotaLabel(enabledCount: number): { enabled: number; quota: number | null } {
        return formatDocksiteQuotaLabel(enabledCount, this.moduleLicenses);
    }

    public countDocksiteEnabledUsers(users: Array<{ docksiteEnabled?: boolean }>): number {
        return countDocksiteEnabledUsers(users);
    }
}
